/** Standard hours per day — used for shift-summary overtime when the API does not send overtimeHours. */
export const STANDARD_DAILY_WORK_HOURS = 8;

/** Mirrors backend AttendanceEventType */
export const AttendanceEventType = {
  CheckIn: 0,
  CheckOut: 1,
};

/** AttendanceClientDeviceType */
export const AttendanceClientDeviceType = {
  Unknown: 0,
  Desktop: 1,
  Mobile: 2,
  Tablet: 3,
  Web: 4,
  Other: 5,
  Biometric: 6,
};

/** Mirrors backend CustomEnums.AttendanceWorkplaceType (Office, Remote, FieldVisit) */
export const AttendanceWorkplaceType = {
  Office: 0,
  Remote: 1,
  FieldVisit: 2,
};

/** Company attendance reporting period (Attendance/Reporting/* APIs). */
export const AttendanceReportingPeriodMode = {
  Today: 0,
  Weekly: 1,
  Monthly: 2,
};

/**
 * Maps clock modal segment (Office / Remote / Field) to API workplace type.
 * @param {'regular' | 'remote' | 'field'} kind
 */
export const workplaceTypeFromAttendanceKind = (kind) => {
  if (kind === "remote") return AttendanceWorkplaceType.Remote;
  if (kind === "field") return AttendanceWorkplaceType.FieldVisit;
  return AttendanceWorkplaceType.Office;
};

/**
 * @param {unknown} workplaceType
 * @returns {'regular' | 'remote' | 'field'}
 */
export const attendanceKindFromWorkplaceType = (workplaceType) => {
  const n = Number(workplaceType);
  if (n === AttendanceWorkplaceType.Remote) return "remote";
  if (n === AttendanceWorkplaceType.FieldVisit) return "field";
  return "regular";
};

/** AttendanceChangeRequestType */
export const AttendanceChangeRequestType = {
  None: 0,
  Edit: 1,
  Delete: 2,
  /** Cancel a pending request via SubmitChangeRequest (matches backend enum). */
  Withdraw: 3,
};

/** AttendanceChangeRequestStatus */
export const AttendanceChangeRequestStatus = {
  None: 0,
  Pending: 1,
  Approved: 2,
  Rejected: 3,
  Withdrawn: 4,
};

/** Mirrors backend AttendanceChangeRequestPeriodMode (approver queue paged API). */
export const AttendanceChangeRequestPeriodMode = {
  Range: 0,
  Monthly: 1,
  Weekly: 2,
};

export const attendanceKindLabel = (eventType) => {
  const n = Number(eventType);
  if (n === AttendanceEventType.CheckOut) return "Check-out";
  return "Check-in";
};

/** Human-readable workplace for DTO display (Office / Remote / Field visit). */
export const workplaceTypeDisplayLabel = (value) => {
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  if (n === AttendanceWorkplaceType.Remote) return "Remote";
  if (n === AttendanceWorkplaceType.FieldVisit) return "Field visit";
  return "Office";
};

export const changeRequestStatusLabel = (status) => {
  const s = String(status ?? "").toLowerCase();
  if (s === "pending" || Number(status) === AttendanceChangeRequestStatus.Pending) return "Pending";
  if (s === "approved" || Number(status) === AttendanceChangeRequestStatus.Approved) return "Approved";
  if (s === "rejected" || Number(status) === AttendanceChangeRequestStatus.Rejected) return "Rejected";
  if (s === "withdrawn" || Number(status) === AttendanceChangeRequestStatus.Withdrawn) return "Withdrawn";
  return "—";
};

/**
 * Maps paged/list DTO fields: prefer API `StatusLabel` and `IsAwaitingApproval`; fall back when omitted (older servers).
 * @param {unknown} status — raw `Status` (numeric enum or string)
 * @param {unknown} statusLabelRaw
 * @param {unknown} isAwaitingApprovalRaw
 * @returns {{ statusLabel: string | null, isAwaitingApproval: boolean }}
 */
export const deriveChangeRequestListItemUiFields = (status, statusLabelRaw, isAwaitingApprovalRaw) => {
  const trimmed = String(statusLabelRaw ?? "").trim();
  const statusLabel = trimmed ? trimmed : null;
  const isAwaitingApproval =
    typeof isAwaitingApprovalRaw === "boolean"
      ? isAwaitingApprovalRaw
      : (() => {
          const s = String(status ?? "").toLowerCase();
          return s === "pending" || Number(status) === AttendanceChangeRequestStatus.Pending;
        })();
  return { statusLabel, isAwaitingApproval };
};

/**
 * Table/modal label: prefer server `statusLabel` so numeric status (e.g. withdrawn = 4) never surfaces as "4" in UI.
 * @param {{ status?: unknown, statusLabel?: string | null }} row
 */
export const changeRequestListItemStatusDisplay = (row) => {
  if (!row || typeof row !== "object") return "—";
  const sl = /** @type {{ statusLabel?: string | null }} */ (row).statusLabel;
  if (sl != null && String(sl).trim() !== "") return String(sl).trim();
  return changeRequestStatusLabel(/** @type {{ status?: unknown }} */ (row).status);
};

/**
 * Approve/Reject visibility: prefer server `isAwaitingApproval`.
 * @param {{ status?: unknown, isAwaitingApproval?: boolean }} row
 */
export const changeRequestListItemIsAwaitingApproval = (row) => {
  if (!row || typeof row !== "object") return false;
  if (typeof row.isAwaitingApproval === "boolean") return row.isAwaitingApproval;
  const s = String(row.status ?? "").toLowerCase();
  return s === "pending" || Number(row.status) === AttendanceChangeRequestStatus.Pending;
};

/**
 * Day/Today API attaches `changeRequest` per event. While status is Pending, do not submit another change request.
 * @param {unknown} ev normalized attendance event
 */
export const hasPendingAttendanceChangeRequest = (ev) => {
  if (!ev || typeof ev !== "object") return false;
  const cr = /** @type {{ changeRequest?: { status?: unknown } }} */ (ev).changeRequest;
  if (!cr || typeof cr !== "object") return false;
  const st = cr.status;
  return Number(st) === AttendanceChangeRequestStatus.Pending || String(st ?? "").toLowerCase() === "pending";
};

/**
 * `changeRequest.requestType` from day/today APIs while status is pending.
 * @param {unknown} ev
 * @returns {number | null} Numeric `AttendanceChangeRequestType`, or null if not pending / missing.
 */
export const getPendingChangeRequestType = (ev) => {
  if (!hasPendingAttendanceChangeRequest(ev) || !ev || typeof ev !== "object") return null;
  const cr = /** @type {{ changeRequest?: { requestType?: unknown, RequestType?: unknown } }} */ (ev)
    .changeRequest;
  if (!cr || typeof cr !== "object") return null;
  const rt = cr.requestType ?? cr.RequestType;
  const n = Number(rt);
  return Number.isFinite(n) ? n : null;
};
